// data/language-store.js — En/Zh preference, persisted via progress-store
// settings.
//
// Mirrors `Assets/_App/Engines/LanguageManager.cs` from the Unity port, but
// uses IndexedDB-backed settings instead of ISecureStorage. This is the
// *content language* (which language the item.title and lyrics lines
// display in), not the UI string language (which is core/i18n.js).

import { getSetting, setSetting } from "./progress-store.js";

/** @typedef {"En"|"Zh"} AppLanguage */

const KEY = "appLanguage";
/** @type {AppLanguage} */ let current = "En";
/** @type {Set<(lang: AppLanguage) => void>} */ const listeners = new Set();

/** Load the persisted preference. Called once at boot. */
export async function loadLanguagePreference() {
  const stored = await getSetting(KEY);
  if (stored === "En" || stored === "Zh") current = stored;
  return current;
}

/** @returns {AppLanguage} */
export function getLanguage() { return current; }

/** @param {AppLanguage} lang */
export async function setLanguage(lang) {
  if (lang !== "En" && lang !== "Zh") return;
  if (current === lang) return;
  current = lang;
  await setSetting(KEY, lang);
  for (const fn of listeners) fn(current);
}

/** Toggle between En and Zh. */
export async function toggle() {
  await setLanguage(current === "En" ? "Zh" : "En");
}

/**
 * Pick the localised string for the current language. Mirrors
 * LanguageManager.Pick() in the Unity port.
 * @param {string|undefined|null} en
 * @param {string|undefined|null} zh
 * @returns {string}
 */
export function pick(en, zh) {
  if (current === "Zh" && zh) return zh;
  return en ?? "";
}

/**
 * Subscribe to language changes.
 * @param {(lang: AppLanguage) => void} fn
 * @returns {() => void} unsubscribe
 */
export function subscribe(fn) {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}
